import { useContext, useEffect, useState } from "react";
import { Button, Card, Col, Form, Row } from "react-bootstrap";
import Alert from "react-bootstrap/Alert";

import NewClientPortal from './NewClientPortal';
import StateContext from '../../context/State';
import PortalDataService from '../../services/portal.service';
import LearnMoreComponent from '../../components/LearnMoreComponent';
import ESigningCard from '../../components/ESigningCard';
import { useAppSelector } from '../../../../redux/hooks';
import { lddStateObject } from '../../../../redux/reducer';
import { RPWDataReader, getDataReader, getDocumentSavingPreference } from '../../../../livedataFuncs';

const PROVINCES_WITH_FULL_NAME = ["MB", "BC", "NS", "SK"];

const Initiate = () => {
  const { state } = useContext(StateContext);
  const dataService = new PortalDataService(state);

  const [showModal, setShowModal] = useState<boolean>(false);
  const [showModalLearnMore, setShowModalLearnMore] = useState<boolean>(false);
  const [contactInformationSet, setContactInformationSet] =
    useState<boolean>(false);
  const [isClientEstate, setIsClientEstate] = useState<boolean>(false);
  const livedataRootContainer = useAppSelector(
    (state: lddStateObject) => state.currentNavigation.livedataRootContainer,
  );
  const processor: RPWDataReader = getDataReader(
    livedataRootContainer?.dataRoot,
  );
  const isPrivateLenderFile: string = processor.getActingForLenderOnly();

  const isSaleFile: boolean = processor.getPSM() === "S";

  const [selectedOptions, setSelectedOptions] = useState({
    RequestIDNumber: "1" as "1" | "2",
    clientConnectId: true,
    addIntakeFields: true,
    retainerAgreement: true,
    consentForm: true,
    documentsRequested: true,
    sourceOfFunds: !isSaleFile,
    remoteClosingCalls: true,
    requireFaceImage: true,
    selectAll: true,
  });

  const spouseInfo = processor.getSpouseNotOnTitleInfo();
  const showSpouseEmailAlert =
    spouseInfo &&
    spouseInfo.length > 0 &&
    spouseInfo.some(
      (spouse) =>
        spouse.SpouseName !== null &&
        spouse.SpouseName !== "" &&
        (spouse.SpouseEmail === null || spouse.SpouseEmail === ""),
    );

  const isAbleToCreatePortal = async () => {
    try {
      const isAbleResponse = await dataService.ableToCreatePortal();
      setContactInformationSet(isAbleResponse.status);
      setIsClientEstate(isAbleResponse.isEstate);
    } catch (e) {
      console.log("Unable to get info on whether or not a CP can be made: ", e);
    }
  };

  useEffect(() => {
    isAbleToCreatePortal();
  }, []);

  const handleOptionChange = (optionName) => {
    if (optionName === "selectAll") {
      handleSelectAll();
    } else {
      setSelectedOptions((prev) => ({
        ...prev,
        [optionName]: !prev[optionName],
        selectAll: !["clientConnectId", "requireFaceImage"].includes(optionName)
          ? false
          : prev.selectAll,
      }));
    }
  };

  const handleRequestIDNumberChange = (value: "1" | "2") => {
    setSelectedOptions((prev) => ({
      ...prev,
      RequestIDNumber: value,
    }));
  };

  const handleSelectAll = () => {
    const selectableOptions = [
      "addIntakeFields",
      "retainerAgreement",
      "consentForm",
      "documentsRequested",
      "remoteClosingCalls",
    ];
    if (isSaleFile) {
      const allSelected = selectableOptions.every(
        (key) => selectedOptions[key],
      );
      setSelectedOptions((prev) => ({
        ...prev,
        clientConnectId: prev.clientConnectId,
        requireFaceImage: prev.requireFaceImage,
        addIntakeFields: !allSelected,
        retainerAgreement: !allSelected,
        consentForm: !allSelected,
        documentsRequested: !allSelected,
        sourceOfFunds: false, // Always false in sale files
        remoteClosingCalls: !allSelected,
        selectAll: !allSelected,
      }));
    } else {
      // In non-sale files, include sourceOfFunds in the check
      const allSelected = [...selectableOptions, "sourceOfFunds"].every(
        (key) => selectedOptions[key],
      );
      setSelectedOptions((prev) => ({
        ...prev,
        clientConnectId: prev.clientConnectId,
        requireFaceImage: prev.requireFaceImage,
        addIntakeFields: !allSelected,
        retainerAgreement: !allSelected,
        consentForm: !allSelected,
        documentsRequested: !allSelected,
        sourceOfFunds: !allSelected,
        remoteClosingCalls: !allSelected,
        selectAll: !allSelected,
      }));
    }
  };

    const renderCheckbox = (option: string, label: string, style?: { marginTop?: string }, noBorder?: boolean) => {
      return (
            <div 
               className={`mb-2 ${noBorder ? 'border-0' : ''}`}
              style={{ 
                  ...style, 
                  backgroundColor: noBorder ? undefined : 'var(--bs-primary-bg-subtle)',
                  padding: noBorder ? undefined : '10px 12px',
                  borderRadius: noBorder ? undefined : '8px'
              }}
            >
              <Form.Check 
                  type="checkbox"
                  id={option}
                  label={label}
                  checked={selectedOptions[option]}
                  onChange={() => handleOptionChange(option)}
              />
            </div>
      );
  };

  const isAnyRequiredOptionSelected = () => {
    return (
      selectedOptions.clientConnectId ||
      selectedOptions.addIntakeFields ||
      selectedOptions.retainerAgreement ||
      selectedOptions.consentForm ||
      selectedOptions.documentsRequested
    );
  };

  const handleInitiation = () => {
    const message =
      "ClientConnect is currently not available in transactions where you are representing ";
    if (isClientEstate) {
      alert(message + "an Estate");
      return;
    }
    if (isPrivateLenderFile && isPrivateLenderFile === "1") {
      alert(message + "a Lender");
      return;
    }
    setShowModal(true);
  };

    return (
      <>

      <ESigningCard />

      <Card>

        <Card.Body>
        
            {showSpouseEmailAlert && (
                <Alert variant="warning" className="mb-3">
                    One of your spouse not on title does not have an email address. If you would like the spouse (not on title) to receive a ClientConnect invitation, please add an email address for the spouse.
                </Alert>
            )}
        
            {!contactInformationSet && (
                <Alert variant="danger">
                    {PROVINCES_WITH_FULL_NAME.includes(dataService.getProvince()) 
                        ? <>Please add a client's <b>full name</b> and <b>email</b> to initiate ClientConnect.</>
                        : <>Please add a client's <b>name</b> and <b>email</b> to initiate ClientConnect.</>
                    }
                </Alert>
            )}

          <Row>
            <Col>
              <Row className="mb1">
                <Col className="d-flex align-items-center"> 
                    <Card.Title>Identity Verification</Card.Title>
                </Col>

                <Col xs={8}>
                  <Row>
                    <Col xs="auto" className="d-flex align-items-center">
                      <Card.Title>Additional Features</Card.Title>
                    </Col>
                    <Col>
                        {renderCheckbox('selectAll', 'Select all additional features')}
                    </Col>
                  </Row>
                </Col>
              </Row>

              <Row className="mb-1">
                <Col>
                  <div className="pane connect">
                      <img src="images/cclogo.png" style={{ height: '55px', margin: '10px auto 15px' }} alt="CC Logo" />
                          <p>
                              <i className="fas fa-solid fa-circle-check text-success" style={{ marginRight: "10px" }}></i>
                              Unlimited ID Submissions
                          </p>

                          <p><b>Included in Realti Fee</b></p>

                      <div style={{ display: 'inline-block', margin: '5px auto'}}>
                          {['AB','ON','NS','BC'].includes(dataService.getProvince()) && 
                          renderCheckbox('requireFaceImage', 'Require Face Image', {}, true)}
                      </div>  

                      <Button 
                          variant="outline-primary"
                          onClick={() => handleOptionChange('clientConnectId')}>
                              {selectedOptions.clientConnectId ?
                                  <>
                                      <i className="fas fa-check text-success"></i>
                                      Selected
                                  </>
                                  :
                                  <>
                                      Use
                                  </>
                              }
                      </Button>
                  </div>
                </Col>

                <Col xs={8}>
                  <Row classname="mb-1">
                    <Col>
                        <Alert>Select any features you would like to add to ClientConnect. You can add features now, or at any point after initiation.</Alert>
                    </Col>
                  </Row>

                  <Row>
                    <Col>
                        {renderCheckbox('addIntakeFields', 'Client Intake Forms')}
                    </Col>

                    <Col>
                      {renderCheckbox('retainerAgreement', 'Written Retainers')}
                    </Col>
                  </Row>

                  <Row>  
                    <Col>
                      {renderCheckbox('documentsRequested', 'Document Exchange')}
                    </Col>                
                
                    <Col>
                      {renderCheckbox('remoteClosingCalls', 'Video Calls')}
                    </Col>
                  </Row>

                  <Row>
                    <Col>
                      {!isSaleFile && renderCheckbox('sourceOfFunds', 'Source of Funds')}
                    </Col>
                      
                    <Col>
                      {renderCheckbox('consentForm', 'Information Sharing Consent Form')}
                    </Col>
                  </Row>
                </Col>
              </Row>
            </Col>
          </Row>
        </Card.Body>

        <Card.Footer>
          <Row>
            <Col className="align-right">
              <Button
                variant="primary"
                disabled={
                  !contactInformationSet || !isAnyRequiredOptionSelected()
                }
                onClick={handleInitiation}
              >
                Initiate ClientConnect
              </Button>
            </Col>
          </Row>
        </Card.Footer>
      </Card>
      <>
        <NewClientPortal
          showModal={showModal}
          onHideModal={() => setShowModal(false)}
          selectedOptions={selectedOptions}
          onRequestIDNumberChange={handleRequestIDNumberChange}
        />
        <LearnMoreComponent
          showModalLearnMore={showModalLearnMore}
          onHideModal={() => setShowModalLearnMore(false)}
          province={dataService.getProvince()}
        />
      </>
    </>
  );
};

export default Initiate;
